import React from 'react';
import { Document, Page, Text, View, StyleSheet, Font, Image } from '@react-pdf/renderer';
import appLogo from '../../../assets/logo/IconOnly_Transparent_NoBuffer.png';
import RtlText from './RtlText';
import type { NutritionPdfData, NutritionPdfFormula, NutritionPdfTable } from './types';

// Register Hebrew font (same family/registration used by the other PDFs in the repo).
// Anything mixing Hebrew with numbers, Latin or punctuation must go through <RtlText>,
// which see — the renderer's LTR base direction otherwise puts the pieces on the
// wrong side of the phrase.
Font.register({
    family: 'Noto',
    fonts: [
        { src: '/fonts/noto/static/NotoSansHebrew-Regular.ttf', fontWeight: 400 },
        { src: '/fonts/noto/static/NotoSansHebrew-Bold.ttf', fontWeight: 700 },
    ],
});

// Palette lifted from PreemieNutrition.scss so the PDF matches the desktop page.
const COLORS = {
    text: '#1a3b5d',
    primary: '#103C6E',
    teal: '#26A69A',
    tealDark: '#136D62',
    inputPanel: '#EEFFFD',
    formulaRow: '#F8FFFE',
    tableAlt: '#E6F7F4',
    muted: '#546e7a',
};

const styles = StyleSheet.create({
    page: {
        paddingTop: 20,
        paddingBottom: 24,
        paddingHorizontal: 24,
        fontFamily: 'Noto',
        backgroundColor: 'white',
        color: COLORS.text,
        fontSize: 9,
    },
    header: {
        flexDirection: 'row-reverse',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: 10,
        minHeight: 26,
    },
    headerSide: {
        width: 110,
    },
    headerDate: {
        fontSize: 8,
        color: COLORS.muted,
        textAlign: 'right',
    },
    headerTitleContainer: {
        flex: 1,
        alignItems: 'center',
    },
    headerTitle: {
        fontSize: 15,
        fontWeight: 'bold',
        color: COLORS.text,
        textAlign: 'center',
    },
    headerSubtitle: {
        fontSize: 10,
        color: COLORS.primary,
        marginTop: 3,
        textAlign: 'center',
    },
    headerLogos: {
        width: 110,
        flexDirection: 'row',
        justifyContent: 'flex-start',
        alignItems: 'center',
        height: 26,
    },
    appLogo: {
        maxHeight: 26,
        objectFit: 'contain',
    },

    // Basic info strip — mirrors the ".global-inputs" panel
    infoStrip: {
        flexDirection: 'row-reverse',
        backgroundColor: COLORS.inputPanel,
        borderRadius: 8,
        paddingVertical: 6,
        paddingHorizontal: 10,
        marginBottom: 10,
    },
    infoItem: {
        flexDirection: 'row-reverse',
        alignItems: 'center',
        marginLeft: 22,
    },
    infoLabel: {
        fontSize: 9,
        fontWeight: 'bold',
        color: COLORS.text,
        marginLeft: 5,
    },
    infoValue: {
        fontSize: 9,
        color: COLORS.primary,
    },

    columns: {
        flexDirection: 'row-reverse',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
    },
    column: {
        width: '49%',
    },

    sectionTitle: {
        fontSize: 11,
        fontWeight: 'bold',
        color: COLORS.text,
        marginBottom: 5,
        textAlign: 'right',
    },

    // Formula card — mirrors ".formula-row"
    formulaCard: {
        backgroundColor: COLORS.formulaRow,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: '#E0F2F1',
        borderStyle: 'solid',
        padding: 8,
        marginBottom: 6,
    },
    formulaHeader: {
        flexDirection: 'row-reverse',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 4,
    },
    formulaName: {
        fontSize: 10,
        fontWeight: 'bold',
        color: COLORS.text,
    },
    badge: {
        fontSize: 7,
        color: 'white',
        backgroundColor: COLORS.teal,
        borderRadius: 6,
        paddingVertical: 2,
        paddingHorizontal: 6,
    },
    formulaLine: {
        flexDirection: 'row-reverse',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingVertical: 2,
        borderBottomWidth: 1,
        borderBottomColor: '#EFF7F6',
        borderBottomStyle: 'solid',
    },
    formulaLineLabel: {
        fontSize: 8,
        color: COLORS.muted,
    },
    formulaLineValue: {
        fontSize: 8,
        fontWeight: 'bold',
        color: COLORS.primary,
    },
    instructions: {
        marginTop: 4,
        backgroundColor: '#f5f5f5',
        borderRadius: 4,
        padding: 4,
        fontSize: 7,
        color: '#666',
        textAlign: 'right',
    },

    // Summary card — mirrors ".summary-card"
    summaryCard: {
        backgroundColor: 'white',
        borderTopWidth: 3,
        borderBottomWidth: 3,
        borderTopColor: COLORS.tealDark,
        borderBottomColor: COLORS.tealDark,
        borderStyle: 'solid',
        paddingVertical: 8,
        paddingHorizontal: 10,
        alignItems: 'center',
        marginBottom: 10,
    },
    summaryTitle: {
        fontSize: 12,
        fontWeight: 'bold',
        color: COLORS.text,
        marginBottom: 4,
    },
    summaryLine: {
        fontSize: 9,
        color: COLORS.text,
        marginBottom: 2,
    },
    // "GIR counts TPN only" — a small qualifier right under the GIR line.
    summaryGirNote: {
        fontSize: 7,
        color: '#607D8B',
        marginBottom: 2,
    },
    // Caveat explaining that a powder additive is outside the volume above.
    summaryNote: {
        fontSize: 7,
        color: '#607D8B',
        textAlign: 'center',
        lineHeight: 1.4,
        marginTop: 5,
        paddingTop: 4,
        borderTopWidth: 1,
        borderTopColor: '#E0E0E0',
        borderTopStyle: 'solid',
    },

    // Totals table — mirrors ".totals-table"
    tableBlock: {
        marginBottom: 10,
    },
    tableTitle: {
        fontSize: 11,
        fontWeight: 'bold',
        color: COLORS.text,
        marginBottom: 4,
        textAlign: 'right',
    },
    table: {
        borderRadius: 4,
        overflow: 'hidden',
    },
    tableHeaderRow: {
        flexDirection: 'row-reverse',
        backgroundColor: COLORS.teal,
    },
    tableHeaderCell: {
        paddingVertical: 4,
        paddingHorizontal: 6,
        fontSize: 8,
        fontWeight: 'bold',
        color: 'white',
    },
    tableRow: {
        flexDirection: 'row-reverse',
        borderBottomWidth: 1,
        borderBottomColor: '#f0f0f0',
        borderBottomStyle: 'solid',
    },
    tableRowLast: {
        backgroundColor: COLORS.tableAlt,
        borderBottomWidth: 0,
    },
    tableCell: {
        paddingVertical: 3,
        paddingHorizontal: 6,
        fontSize: 8,
        color: COLORS.text,
    },
    cellLabel: { width: '46%', textAlign: 'right' },
    cellLabelText: { fontSize: 8, color: COLORS.text },
    cellTotal: { width: '27%', textAlign: 'right' },
    cellPerKg: { width: '27%', textAlign: 'right' },
    emptyNote: {
        fontSize: 8,
        color: COLORS.muted,
        textAlign: 'right',
        marginBottom: 6,
    },
});

const formatDateTime = (date: Date) => {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}  ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const FormulaCard: React.FC<{ formula: NutritionPdfFormula }> = ({ formula }) => (
    <View style={styles.formulaCard} wrap={false}>
        <View style={styles.formulaHeader}>
            <RtlText style={styles.formulaName}>{formula.name}</RtlText>
            {/* Plain Text: the badge is a single pill, and RtlText would paint one per token */}
            {formula.badge ? <Text style={styles.badge}>{formula.badge}</Text> : null}
        </View>
        {formula.lines.map((line, idx) => (
            <View key={idx} style={styles.formulaLine}>
                <RtlText style={styles.formulaLineLabel}>{line.label}</RtlText>
                <RtlText style={styles.formulaLineValue}>{line.value}</RtlText>
            </View>
        ))}
        {/* Plain Text: free-text instructions need to wrap, which RtlText deliberately can't */}
        {formula.instructions ? (
            <Text style={styles.instructions}>{`פירוט: ${formula.instructions}`}</Text>
        ) : null}
    </View>
);

// wrap={false} keeps a table whole — without it a summary splits across pages and
// the continuation rows lose their header.
const TotalsTable: React.FC<{ table: NutritionPdfTable }> = ({ table }) => (
    <View style={styles.tableBlock} wrap={false}>
        <RtlText style={styles.tableTitle}>{table.title}</RtlText>
        <View style={styles.table}>
            <View style={styles.tableHeaderRow}>
                <Text style={[styles.tableHeaderCell, styles.cellLabel]}>ערך</Text>
                <Text style={[styles.tableHeaderCell, styles.cellTotal]}>סה"כ ליום</Text>
                <Text style={[styles.tableHeaderCell, styles.cellPerKg]}>לק"ג</Text>
            </View>
            {table.rows.map((row, idx) => {
                const isLast = idx === table.rows.length - 1;
                return (
                    <View key={idx} style={[styles.tableRow, ...(isLast ? [styles.tableRowLast] : [])]}>
                        <View style={[styles.tableCell, styles.cellLabel]}>
                            <RtlText style={styles.cellLabelText}>{row.label}</RtlText>
                        </View>
                        <Text style={[styles.tableCell, styles.cellTotal]}>{row.total}</Text>
                        <Text style={[styles.tableCell, styles.cellPerKg]}>{row.perKg}</Text>
                    </View>
                );
            })}
        </View>
    </View>
);

const NutritionPDFDocument: React.FC<{ data: NutritionPdfData }> = ({ data }) => {
    // The first table sits beside the formulas (like the desktop results column);
    // any breakdown tables go full-width underneath so they never have to paginate
    // inside a multi-column row — react-pdf drops content when that happens.
    const [primaryTable, ...breakdownTables] = data.tables;

    return (
        <Document>
            <Page size="A4" style={styles.page}>
                <View style={styles.header} fixed>
                    <View style={styles.headerSide}>
                        <Text style={styles.headerDate}>{formatDateTime(new Date())}</Text>
                    </View>
                    <View style={styles.headerTitleContainer}>
                        <RtlText style={styles.headerTitle} align="center">{data.title}</RtlText>
                        {data.patientInfo ? (
                            <RtlText style={styles.headerSubtitle} align="center">{data.patientInfo}</RtlText>
                        ) : null}
                    </View>
                    <View style={styles.headerLogos}>
                        <Image src={appLogo} style={styles.appLogo} />
                    </View>
                </View>

                <View style={styles.infoStrip}>
                    <View style={styles.infoItem}>
                        <RtlText style={styles.infoLabel}>משקל:</RtlText>
                        <RtlText style={styles.infoValue}>{data.weight || '-'}</RtlText>
                    </View>
                    <View style={styles.infoItem}>
                        <RtlText style={styles.infoLabel}>סוג הזנה:</RtlText>
                        <RtlText style={styles.infoValue}>{data.feedingType || '-'}</RtlText>
                    </View>
                </View>

                <View style={styles.columns}>
                    {/* Right column (first in RTL): the formulas the user entered */}
                    <View style={styles.column}>
                        {data.tpnFormulas.length > 0 && (
                            <View>
                                <Text style={styles.sectionTitle}>TPN</Text>
                                {data.tpnFormulas.map((formula, idx) => (
                                    <FormulaCard key={`tpn-${idx}`} formula={formula} />
                                ))}
                            </View>
                        )}

                        {data.enteralFormulas.length > 0 && (
                            <View>
                                <Text style={styles.sectionTitle}>אנטרלית</Text>
                                {data.enteralFormulas.map((formula, idx) => (
                                    <FormulaCard key={`enteral-${idx}`} formula={formula} />
                                ))}
                            </View>
                        )}

                        {data.tpnFormulas.length === 0 && data.enteralFormulas.length === 0 && (
                            <Text style={styles.emptyNote}>לא הוזנו פורמולות</Text>
                        )}
                    </View>

                    {/* Left column: summary card + the headline totals table */}
                    <View style={styles.column}>
                        <View style={styles.summaryCard}>
                            <Text style={styles.summaryTitle}>סיכום הזנה</Text>
                            <RtlText style={styles.summaryLine} align="center">{`סה"כ נפח ליום: ${data.summary.volume} מ"ל ליום`}</RtlText>
                            <RtlText style={styles.summaryLine} align="center">{`נפח לק"ג: ${data.summary.volumePerKg} מ"ל`}</RtlText>
                            {data.summary.gir ? (
                                <RtlText style={styles.summaryLine} align="center">{data.summary.gir}</RtlText>
                            ) : null}
                            {data.summary.girNote ? (
                                <RtlText style={styles.summaryGirNote} align="center">{data.summary.girNote}</RtlText>
                            ) : null}
                            {/* Plain Text: a whole sentence needs to wrap, which RtlText deliberately can't */}
                            {data.summary.note ? (
                                <Text style={styles.summaryNote}>{data.summary.note}</Text>
                            ) : null}
                        </View>

                        {primaryTable && <TotalsTable table={primaryTable} />}
                    </View>
                </View>

                {breakdownTables.length > 0 && (
                    <View style={styles.columns}>
                        {breakdownTables.map((table, idx) => (
                            <View key={idx} style={styles.column}>
                                <TotalsTable table={table} />
                            </View>
                        ))}
                    </View>
                )}
            </Page>
        </Document>
    );
};

export default NutritionPDFDocument;
